import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  Divider,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import { HiPencil, HiTrash, HiPlus } from "react-icons/hi";
import { useSnackbar } from "notistack";
import { useQuery } from "@tanstack/react-query";
import {
  useAdminUpdateUser,
  useAdminIdTypes,
  useAdminUserRoleAssignments,
  useAdminAllOrganizations,
  useAdminRolesByOrg,
  useAdminCreateAssignment,
  useAdminDeleteAssignment,
  adminKeys,
} from "@/api/hooks/admin.hooks";
import { api } from "@/api/client/axiosClient";
import { ENDPOINTS } from "@/api/endpoints";
import type { UserDto, IdTypeDto, OrganizationDto } from "@/api/types/dtos";
import type { RoleDto } from "@/api/types/dtos/role-privilege.dto";

interface EditUserModalProps {
  open: boolean;
  user: UserDto | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ open, user, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const updateMutation = useAdminUpdateUser();

  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [nickName, setNickName] = useState("");
  const [idType, setIdType] = useState<IdTypeDto | null>(null);
  const [idNumber, setIdNumber] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [gender, setGender] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [assignOrg, setAssignOrg] = useState<OrganizationDto | null>(null);
  const [assignRole, setAssignRole] = useState<RoleDto | null>(null);
  const [assigningRole, setAssigningRole] = useState(false);

  const { data: idTypesData } = useAdminIdTypes();
  const idTypes: IdTypeDto[] = useMemo(
    () => (idTypesData?.data ?? []) as IdTypeDto[],
    [idTypesData]
  );

  const { data: assignmentsData, isLoading: assignmentsLoading } = useAdminUserRoleAssignments(
    user?.id || null
  );
  const rawAssignments = useMemo(
    () =>
      (
        (assignmentsData?.data ?? []) as Array<{
          id: string;
          name: string;
          roleId: string;
          isActive: boolean;
        }>
      ).filter((a) => a.isActive),
    [assignmentsData]
  );

  const { data: orgsData } = useAdminAllOrganizations();
  const organizations: OrganizationDto[] = useMemo(
    () => (orgsData?.data ?? []) as OrganizationDto[],
    [orgsData]
  );

  const { data: assignRolesData, isLoading: assignRolesLoading } = useAdminRolesByOrg(
    assignOrg?.id || ""
  );
  const assignRoles: RoleDto[] = useMemo(
    () => (assignRolesData?.data ?? []) as RoleDto[],
    [assignRolesData]
  );

  const createAssignment = useAdminCreateAssignment();
  const deleteAssignment = useAdminDeleteAssignment();

  const uniqueRoleIds = useMemo(
    () => [...new Set(rawAssignments.map((a) => a.roleId))],
    [rawAssignments]
  );

  const { data: roleDetailsMap } = useQuery({
    queryKey: [...adminKeys.all, "roleDetails", uniqueRoleIds],
    queryFn: async () => {
      const map = new Map<string, RoleDto>();
      await Promise.allSettled(
        uniqueRoleIds.map(async (roleId) => {
          const res = await api.get<{ data: RoleDto }>(ENDPOINTS.roles.byId(roleId), {
            headers: { "X-Organization-Id": "" },
          });
          const role = res?.data;
          if (role) map.set(roleId, role);
        })
      );
      return map;
    },
    enabled: open && uniqueRoleIds.length > 0,
    staleTime: 60000,
  });

  const enrichedAssignments = useMemo(
    () =>
      rawAssignments.map((a) => {
        const role = roleDetailsMap?.get(a.roleId);
        const org = role ? organizations.find((o) => o.id === role.organizationId) : undefined;
        return {
          userRoleAssignmentId: a.id,
          roleId: a.roleId,
          organizationId: role?.organizationId || "",
          organizationName: org?.name || "—",
          roleName: role?.name || a.name || "—",
        };
      }),
    [rawAssignments, roleDetailsMap, organizations]
  );

  const handleReset = useCallback(() => {
    if (user) {
      setName(user.name || "");
      setLastName(user.lastName || "");
      setMiddleName(user.middleName || "");
      setNickName(user.nickName || "");
      setIdType(
        user.idTypeId
          ? { id: user.idTypeId, name: "", description: "", code: "", isActive: true }
          : null
      );
      setIdNumber(user.idNumber || "");
      setEmail(user.email || "");
      setAddress(user.address || "");
      setBirthdate(user.birthdate ? user.birthdate.split("T")[0] : "");
      setGender(user.gender || "");
      setIsActive(user.isActive);
      setErrors({});
    }
  }, [user]);

  useEffect(() => {
    if (open && user) handleReset();
  }, [open, user, handleReset]);

  useEffect(() => {
    if (user?.idTypeId && idTypes.length > 0) {
      const found = idTypes.find((t) => t.id === user.idTypeId);
      if (found) setIdType(found);
    }
  }, [user?.idTypeId, idTypes]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "El nombre es obligatorio";
    if (!idType) newErrors.idType = "El tipo de documento es obligatorio";
    if (!idNumber.trim()) newErrors.idNumber = "El número de documento es obligatorio";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!user || !validate()) return;

    updateMutation.mutate(
      {
        id: user.id,
        data: {
          id: user.id,
          name: name.trim(),
          lastName: lastName.trim() || undefined,
          middleName: middleName.trim() || undefined,
          nickName: nickName.trim() || undefined,
          idTypeId: idType!.id,
          idNumber: idNumber.trim(),
          email: email.trim() || undefined,
          address: address.trim() || undefined,
          birthdate: birthdate || undefined,
          gender: gender || undefined,
          isActive,
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Usuario actualizado exitosamente", { variant: "success" });
          onSuccess();
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: { data?: { description?: string; message?: string } };
          };
          const message =
            err?.response?.data?.description ||
            err?.response?.data?.message ||
            "Error al actualizar el usuario";
          enqueueSnackbar(message, { variant: "error" });
        },
      }
    );
  };

  const handleClose = () => {
    if (updateMutation.isPending) return;
    setErrors({});
    setAssignOrg(null);
    setAssignRole(null);
    onClose();
  };

  const handleAddAssignment = () => {
    if (!user || !assignOrg || !assignRole) return;
    setAssigningRole(true);
    createAssignment.mutate(
      { userId: user.id, roleId: assignRole.id, organizationId: assignOrg.id },
      {
        onSuccess: () => {
          enqueueSnackbar(`Asignado a "${assignOrg.name}" como "${assignRole.name}"`, {
            variant: "success",
          });
          setAssignOrg(null);
          setAssignRole(null);
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: { data?: { description?: string; message?: string } };
          };
          const message =
            err?.response?.data?.description ||
            err?.response?.data?.message ||
            "Error al asignar el rol";
          enqueueSnackbar(message, { variant: "error" });
        },
        onSettled: () => setAssigningRole(false),
      }
    );
  };

  const handleRemoveAssignment = (userRoleAssignmentId: string, orgId: string) => {
    if (!user) return;
    deleteAssignment.mutate(
      { organizationId: orgId, userId: user.id, userRoleAssignmentId },
      {
        onSuccess: () => {
          enqueueSnackbar("Asignación eliminada", { variant: "success" });
        },
        onError: () => {
          enqueueSnackbar("Error al eliminar la asignación", { variant: "error" });
        },
      }
    );
  };

  const isSubmitting = updateMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-user-dialog-title"
      PaperProps={{
        sx: { borderTop: "4px solid", borderColor: "primary.main" },
      }}
    >
      <DialogTitle id="edit-user-dialog-title" sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <HiPencil size={22} />
          <Box>
            <Typography variant="h6" component="span" fontWeight={600}>
              Editar Usuario
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {[user?.name, user?.lastName].filter(Boolean).join(" ")}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Información Personal
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Nombre"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={isSubmitting}
                  size="small"
                  sx={{ flex: "1 1 200px" }}
                />
                <TextField
                  label="Apellido"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSubmitting}
                  size="small"
                  sx={{ flex: "1 1 200px" }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Segundo Nombre"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  disabled={isSubmitting}
                  size="small"
                  sx={{ flex: "1 1 200px" }}
                />
                <TextField
                  label="Apodo"
                  value={nickName}
                  onChange={(e) => setNickName(e.target.value)}
                  disabled={isSubmitting}
                  size="small"
                  sx={{ flex: "1 1 200px" }}
                />
              </Box>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Documento de Identidad
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Autocomplete
                options={idTypes}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={idType}
                onChange={(_, value) => setIdType(value)}
                disabled={isSubmitting}
                sx={{ flex: "1 1 200px" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo de Documento"
                    required
                    error={!!errors.idType}
                    helperText={errors.idType}
                    size="small"
                  />
                )}
              />
              <TextField
                label="Número de Documento"
                required
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                error={!!errors.idNumber}
                helperText={errors.idNumber}
                disabled={isSubmitting}
                size="small"
                sx={{ flex: "1 1 200px" }}
              />
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Contacto y Ubicación
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Correo Electrónico"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  size="small"
                  sx={{ flex: "1 1 200px" }}
                />
                <TextField
                  label="Fecha de Nacimiento"
                  type="date"
                  value={birthdate}
                  onChange={(e) => setBirthdate(e.target.value)}
                  disabled={isSubmitting}
                  size="small"
                  sx={{ flex: "1 1 200px" }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Dirección"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={isSubmitting}
                  size="small"
                  sx={{ flex: "1 1 200px" }}
                />
                <Autocomplete
                  options={[
                    { value: "M", label: "Masculino" },
                    { value: "F", label: "Femenino" },
                    { value: "O", label: "Otro" },
                  ]}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={
                    gender
                      ? {
                          value: gender,
                          label:
                            gender === "M" ? "Masculino" : gender === "F" ? "Femenino" : "Otro",
                        }
                      : null
                  }
                  onChange={(_, value) => setGender(value?.value || "")}
                  disabled={isSubmitting}
                  sx={{ flex: "1 1 200px" }}
                  renderInput={(params) => <TextField {...params} label="Género" size="small" />}
                />
              </Box>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Estado
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={isSubmitting}
                  color="success"
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={isActive ? "Activo" : "Inactivo"}
                    color={isActive ? "success" : "default"}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              }
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Organizaciones y Roles
            </Typography>

            {assignmentsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : enrichedAssignments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Este usuario no tiene asignaciones de organización o rol.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organización</TableCell>
                      <TableCell>Rol</TableCell>
                      <TableCell align="center" sx={{ width: 60 }}>
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enrichedAssignments.map((a) => (
                      <TableRow key={a.userRoleAssignmentId} hover>
                        <TableCell>
                          <Typography variant="body2">{a.organizationName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={a.roleName}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Eliminar asignación">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleRemoveAssignment(a.userRoleAssignmentId, a.organizationId)
                              }
                              disabled={deleteAssignment.isPending}
                            >
                              <HiTrash size={14} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Agregar nueva asignación
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
              <Autocomplete
                options={organizations}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={assignOrg}
                onChange={(_, value) => {
                  setAssignOrg(value);
                  setAssignRole(null);
                }}
                disabled={assigningRole}
                size="small"
                sx={{ flex: "1 1 200px" }}
                renderInput={(params) => (
                  <TextField {...params} label="Organización" placeholder="Buscar..." />
                )}
              />
              <Autocomplete
                options={assignRoles}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={assignRole}
                onChange={(_, value) => setAssignRole(value)}
                disabled={!assignOrg || assignRolesLoading || assigningRole}
                size="small"
                sx={{ flex: "1 1 200px" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rol"
                    placeholder="Seleccionar..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: assignRolesLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        params.InputProps.endAdornment
                      ),
                    }}
                  />
                )}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddAssignment}
                disabled={!assignOrg || !assignRole || assigningRole}
                startIcon={
                  assigningRole ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <HiPlus size={14} />
                  )
                }
                sx={{ mt: 0.25, minWidth: 100, flexShrink: 0 }}
              >
                {assigningRole ? "..." : "Agregar"}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={isSubmitting} sx={{ minWidth: 100 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : <HiPencil size={16} />
          }
          sx={{ minWidth: 140 }}
        >
          {isSubmitting ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserModal;
